import { ECarriersList, TCarrierFlow } from './Carrier';
import type { claimStatusIds, IClaimCarrierIntegrationInfo } from './Claims';

export interface IClaimState {
	errorCode: number;
	errorMessage: string;
	triggerRefresh: boolean;
	calculated: boolean;
	redirect: boolean;
	claimData: IClaim;
	servicesMap: IService[];
	doctorTypes: { name: string; label: string }[];
	reasonCodesVoid: IReasonCode[];
	reasonCodesCanceled: IReasonCode[];
	reasonCodesPending: IReasonCode[];
	reasonCodesReject: IReasonCode[];
	rekeyClaim?: IClaim;
	reloadData: boolean;
	showClaimComment: boolean;
	validationErrors: IValidationError[];
	statusCalculation: TStatusCalculation;
	otherServices: { serviceLabel: string; serviceId: number | null }[];
}

export type TStatusCalculation =
	| 'beforeCalculation'
	| 'afterCalculation'
	| 'onEdit';

export interface IValidationError {
	field: number;
	message: string;
	value: string;
}

interface IReasonCode {
	code: string;
	groupCode: string;
	groupLabel: string;
	label: string;
	typologyCode: string;
	typologyLabel: string;
}
interface IServiceGroup {
	code: string;
	label: string;
}

export interface IService {
	code: string;
	label: string;
	group: IServiceGroup;
}
/*
	---
	FIXME: this is just a workaround - hoping to be fixed ASAP!
	only for Decare carrier we have to show extended reason codes
	---
*/
export const REASON_CODES_EXTENDED = [
	'11',
	'18',
	'27',
	'29',
	'95',
	'96',
	'119',
	'146',
	'163',
	'185',
	'234',
	'A1',
	'235',
];

export const BASE_REASON_CODE_DUPLICATE = 'duplicate_claim';

export enum REVIEW_FIELDS {
	INVOICE_NUMBER = 'number',
	INVOICE_DATE = 'receiptDate',
	BILLING_ENTITY_NAME = 'name',
	VAT_NUMBER = 'vatCode',
	TOTAL_INVOICE_AMOUNT = 'totalInvoiceAmount',
	RETAIL_NET_OF_TAX = 'retailNetOfTax',
	REVIEW_CONSENT = 'reviewConsent',
	INTERNAL_NOTE = 'internalNote',
}

export enum CLAIM_ACTIONS {
	REJECT = 'REJECT',
	PENDING = 'PENDING',
}

export const RECEIPT_BASE_DATA = {
	[REVIEW_FIELDS.RETAIL_NET_OF_TAX]: '',
	[REVIEW_FIELDS.REVIEW_CONSENT]: false,
	[REVIEW_FIELDS.INTERNAL_NOTE]: '',
};

export interface IClaimReview {
	[REVIEW_FIELDS.INVOICE_NUMBER]: string;
	[REVIEW_FIELDS.INVOICE_DATE]: string;
	[REVIEW_FIELDS.BILLING_ENTITY_NAME]: string;
	[REVIEW_FIELDS.VAT_NUMBER]: string;
	[REVIEW_FIELDS.TOTAL_INVOICE_AMOUNT]: number | string;
	[REVIEW_FIELDS.RETAIL_NET_OF_TAX]: number | string;
	[REVIEW_FIELDS.REVIEW_CONSENT]: boolean;
	[REVIEW_FIELDS.INTERNAL_NOTE]: string;
}

export interface IClaimStatusHistory {
	id: claimStatusIds;
	name: string;
	label: string;
	reasonCode?: string;
	reasonCodeLabel?: string;
	internalNote?: string;
	createdBy?: {
		memberFirstName: string;
		memberLastName: string;
		memberRole: string;
		memberRoleLabel: string;
	};
	lastInsert?: string;
}

export interface IClaimAttachmentHistory {
	id?: number;
	type: 'PRESCRIPTION' | 'RECEIPT' | 'OTHER';
	uuid: string;
	fileName: string;
	lastInsert: string;
	createdBy?: {
		memberFirstName: string;
		memberLastName: string;
		memberRole: string;
		memberRoleLabel: string;
	};
}

export interface IClaimBillingHistory {
	id: number;
	name: string;
	label: string;
	createdBy?: {
		memberFirstName?: string | null;
		memberLastName?: string | null;
		memberRole: string;
		memberRoleLabel: string;
	};
	lastInsert: string;
}

export interface IClaim {
	claimUUID?: string;
	externalClaimId: string;
	memberId: string;
	providerId: string;
	beneficiaryMemberId: string;
	beneficiarySubscriptionUuid: string;
	memberSubscriptionUuid: string;
	claimDate: string;
	step: number;
	// page 1
	exam: number;
	materials: number;
	lensType?: number;
	//page 2
	newPrescriptionContent?: IPrescription;
	oldPrescriptionContent?: IPrescription;
	prescriptionConsent: boolean;
	rxChangedWithoutOldPrescription: boolean | null;
	//page 3
	amount: {
		//basically everything is passed by calculate. only serviceid and retail amount is not
		components: IClaimComponents[];
		total: IClaimComponents;
		accumulator?: IClaimAccumulator;
	};
	rxChangeAttestation?: 0 | 1;
	rxAttestation?: IFileType;
	uploadConsent: boolean;
	attachments: {
		prescription?: IFileType;
		receipt?: IFileType;
		others?: IFileType[];
		complaint?: IFileType;
		fraud?: IFileType;
	};
	complaintDate?: string;
	fraudDate?: string;
	//useless
	memberFullName?: string;
	beneficiaryFullName?: string;
	memberFirstName?: string;
	memberLastName?: string;
	groupName?: string;
	memberProviderLocation?: string;
	status: {
		id: claimStatusIds;
		name: string;
		label: string;
		groupCode: string;
		groupLabel: string;
		internalNote?: string;
		reasonCode: string;
		reasonCodeLabel: string;
	};
	planPayAmount?: number;
	memberPayAmount?: number;
	paidWith?: string;
	memberDesignation?: 'Subscriber' | 'Dependant';
	memberDesignationLabel?: string;
	planName?: string;
	oonProviderInfo?: {
		providerName?: string;
		providerLocation?: string;
		providerPhone?: string;
	};
	reimbursementInfo?: {
		ibanAccount?: string;
		bic?: string;
		bankName?: string;
		bankAddress?: string;
		beneficiaryName?: string;
	};
	signatureId?: number;
	servicesList?: string[];
	reviewClaim: { uuid: string } | null;
	claimInNetwork: boolean;
	carrierIntegrationFlow?: TCarrierFlow;
	receipt: IClaimReview;
	claimCarrierIntegrationInfo: IClaimCarrierIntegrationInfo;
	customLayout: ECarriersList;
	internalNote?: string;
	statusHistory?: IClaimStatusHistory[];
	attachmentHistory?: IClaimAttachmentHistory[];
	billingHistory?: IClaimBillingHistory[];
	returnDetail?: IClaimReturnDetail;
	comments: string | null;
}

export type IClaimComponents = {
	retailAmount: number | string;
	retailNetOfTax: number | string;
	tax: number | string;
	total?: number;
	contractualAdjustmentAmount?: number;
	memberPayAmount?: number;
	planPayAmount?: number;
	serviceName?: string;
	serviceId: null | number;
	discountedAmount?: number;
	assessment?: number;
	allowablePlanPay?: number;
	coPay?: number;
	coInsurance?: number;
	disallowedAmount?: number;
	decliningBalance?: number;
	claimCostReduction?: number;
	inNetworkSave?: number;
	grossRetail?: number | string;
	inStorePromotion?: number | string;
	rowId?: string;
};

export type IPrescription = {
	ava: number;
	prysm: number;
	decentralization: number;
	od: IEyePrescription;
	os: IEyePrescription;
	doctorTypology: string | null;
	prescriptionDate: string | null;
};

export type IEyePrescription = {
	sphere?: string;
	cylinder?: string;
	axis?: string;
	addition?: string;
	prismDioptresIO?: string;
	prismDirectionIO?: string;
	prismDioptresUD?: string;
	prismDirectionUD?: string;
	pd?: string;
	height?: string;
	vertexFitted?: string;
	faceformTilt?: string;
	pantoscopicTilt?: string;
};

export type IClaimAccumulator = {
	benefitId: number;
	benefitType: string;
	accumulatorId: number;
	balance: number;
	maxUsableAmount: number;
	benefitUsed: number;
	planId: number;
	startDate: Date;
	endDate: Date;
	accumulatorType: string;
};

export type IFileType = {
	uuid: string;
	fileName?: string;
	lastInsertTs?: string;
	mime?: string;
	type?: string;
	size?: string;
};

export enum Eye {
	od = 'od',
	os = 'os',
}

export const PRESCRIPTION_DEFAULT = {
	ava: 0,
	prysm: 0,
	decentralization: 0,
	doctorTypology: null,
	prescriptionDate: null,
	od: {
		sphere: '',
		cylinder: '',
		axis: '',
		addition: '',
		prismDioptresIO: '',
		prismDirectionIO: '',
		prismDioptresUD: '',
		prismDirectionUD: '',
		pd: '',
		height: '',
		vertexFitted: '',
		faceformTilt: '',
		pantoscopicTilt: '',
	},
	os: {
		sphere: '',
		cylinder: '',
		axis: '',
		addition: '',
		prismDioptresIO: '',
		prismDirectionIO: '',
		prismDioptresUD: '',
		prismDirectionUD: '',
		pd: '',
		height: '',
		vertexFitted: '',
		faceformTilt: '',
		pantoscopicTilt: '',
	},
};

export enum REASON_CODE_TYPOLOGY {
	VOID = 'void',
	CANCEL = 'cancel',
	PENDING = 'pending',
	REJECT = 'rejected',
}

export interface IAttachmentList {
	documentType: string;
	value: IFileType | undefined;
}

export enum CLAIM_RETURN_ACTIONS {
	RETURN_APPROVED = 'RETURN_APPROVED',
	RETURN_REJECTED = 'RETURN_REJECTED',
}

export interface IClaimReturnDetail {
	status: string;
	statusLabel: string;
	reasonCodeLabel: string;
	attachmentUuid: string;
	attachmentName: string;
}

export const SERVICES_TYPE = {
	LENSES_ONLY: 1,
	FRAME_ONLY: 2,
	EXAM: 3,
	FRAME_AND_LENSES: 4,
	CONTACTS: 5,
	MONOFOCAL_LENS: 12,
	BIFOCAL_LENS: 13,
	TRIFOCAL_LENS: 14,
	PROGRESSIVE_LENS: 15,
	FRAME_AND_MONOFOCAL_LENS: 16,
	FRAME_AND_BIFOCAL_LENS: 17,
	FRAME_AND_TRIFOCAL_LENS: 18,
	FRAME_AND_PROGRESSIVE_LENS: 19,
	EXAM_VDU: 20,
};
